import { PersonRegistrationFieldConfig } from '../types/customer/base';
import { STANDARD_FIELD_LABELS, configurableFieldsFor } from './fieldConfigUtils';

/**
 * What a pré-cadastro still needs before it can be submitted.
 *
 * A pré-cadastro registers the PERSON — name, documents, photos — and deliberately leaves the
 * visit open: where they are going and who is receiving them is decided later, by whoever releases
 * the access. So this answers one question only, "is the person's own record complete?", and the
 * unidade and morador responsável are not part of it by design.
 *
 * Returns the missing requirements by their on-screen labels, all of them, so the operator can be
 * told what is outstanding instead of discovering it one toast at a time.
 */

export interface IdentityRequirementValues {
  firstName?: string;
  lastName?: string;
  cpf?: string;
  rg?: string;
  email?: string;
  phone?: string;
  /** DD/MM/AAAA as typed — plain state behind a mask, with no register() rule of its own. */
  birthDate?: string;
  /** Custom field values keyed by field id. */
  extraData?: Record<string, any>;
}

export interface IdentityRequirementPhotos {
  identity?: unknown;
  documentFront?: unknown;
  documentBack?: unknown;
}

export interface IdentityRequirementInput {
  config: PersonRegistrationFieldConfig | null | undefined;
  photoRequirements?: { identity_photo?: boolean; document_front?: boolean; document_back?: boolean };
  values: IdentityRequirementValues;
  photos?: IdentityRequirementPhotos;
  /**
   * Someone already in the base. Their record exists and already satisfied these, and
   * "Cadastrar Acesso" is not a second onboarding — the submit path skips the same checks.
   */
  isExistingPerson?: boolean;
  /** An admin explicitly allowed registration without a CPF. */
  cpfWaived?: boolean;
}

const blank = (value: unknown): boolean => String(value ?? '').trim() === '';

export function missingIdentityRequirements(input: IdentityRequirementInput): string[] {
  const { config, values, isExistingPerson } = input;
  const photoRequirements = input.photoRequirements ?? {};
  const photos = input.photos ?? {};
  const missing: string[] = [];

  // Not configurable and not optional anywhere: a record with no name is unusable.
  if (blank(values.firstName) || blank(values.lastName)) {
    missing.push('Nome completo');
  }

  // Everything below belongs to a record that already exists.
  if (isExistingPerson) return missing;

  if (config) {
    // Walked in the ADMIN's order, standard and custom together, so the list reads top-to-bottom
    // exactly like the form does. configurableFieldsFor has already dropped disabled fields: one
    // the admin turned off is never asked for, even if it is still flagged required, or the button
    // would sit dead waiting on a value the form does not show.
    for (const item of configurableFieldsFor(config)) {
      if (item.isCustom) {
        const field = (config.custom_fields ?? []).find(f => f.id === item.key);
        if (!field?.required) continue;
        if (blank(values.extraData?.[item.key])) missing.push(field.label || field.id);
        continue;
      }

      if (!config.standard_fields?.[item.key]?.required) continue;
      if (item.key === 'cpf' && input.cpfWaived) continue;

      // The value keys match the config keys one-for-one, except Data de Nascimento, which lives
      // in its own masked state rather than the form.
      const value = item.key === 'date_of_birth' ? values.birthDate : (values as any)[item.key];
      if (blank(value)) missing.push(STANDARD_FIELD_LABELS[item.key] ?? item.key);
    }
  }

  if (photoRequirements.identity_photo && !photos.identity) missing.push('Foto de Identificação');
  if (photoRequirements.document_front && !photos.documentFront) missing.push('Documento (Frente)');
  if (photoRequirements.document_back && !photos.documentBack) missing.push('Documento (Verso)');

  return missing;
}

/**
 * The same list as a sentence, for the line that sits beside the disabled button.
 *
 * A tooltip was not enough: nobody hovers a control that looks dead, so the operator was left
 * guessing which field the form wanted.
 */
export function formatMissingRequirements(missing: string[]): string {
  if (missing.length === 0) return '';
  const verb = missing.length === 1 ? 'Falta' : 'Faltam';
  return `${verb} preencher: ${missing.join(', ')}`;
}
